import { Link } from "react-router-dom";
import { Palette, Clock, ArrowLeft, Check, Sparkles, Copy, Lightbulb } from "lucide-react";
import { Button } from "../../components/ui/button";
import { useState } from "react";
import { toast } from "sonner";

const Article50BestPrompts2026 = () => {
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": "50 Best Coloring Page Prompts [Free Ideas + Examples]",
        "description": "Stop searching endlessly for the perfect coloring page. These 50 tested prompts create beautiful, printable coloring pages every time.",
        "image": "https://colorwonder.app/og-image.png",
        "author": { "@type": "Organization", "name": "ColorWonder" },
        "publisher": {
          "@type": "Organization",
          "name": "ColorWonder",
          "logo": { "@type": "ImageObject", "url": "https://colorwonder.app/logo192.png" }
        },
        "datePublished": "2025-12-26",
        "dateModified": "2025-12-26"
      },
      {
        "@type": "ItemList",
        "name": "50 Best Coloring Page Prompts",
        "description": "Curated list of coloring page prompts for kids, adults, and seasonal themes.",
        "numberOfItems": 50,
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Cute baby dinosaur hatching from egg in sunny meadow" },
          { "@type": "ListItem", "position": 2, "name": "Friendly dragon blowing bubbles instead of fire" },
          { "@type": "ListItem", "position": 3, "name": "Magical unicorn with rainbow mane jumping over clouds" },
          { "@type": "ListItem", "position": 21, "name": "Intricate mandala with lotus flowers and geometric patterns" },
          { "@type": "ListItem", "position": 36, "name": "Christmas village with snow and decorated trees" }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Can I use these prompts with any AI tool?",
            "acceptedAnswer": { "@type": "Answer", "text": "These prompts work with most AI image generators, but they're optimized for ColorWonder which is specifically designed to create clean, printable coloring pages." }
          },
          {
            "@type": "Question",
            "name": "Can I modify these prompts?",
            "acceptedAnswer": { "@type": "Answer", "text": "Absolutely! These are starting points. Feel free to add your own details, combine prompts, or adjust them to match exactly what you're looking for." }
          },
          {
            "@type": "Question",
            "name": "What makes a good coloring page prompt?",
            "acceptedAnswer": { "@type": "Answer", "text": "Good prompts are specific (not generic), include action or setting, and specify the style. Use the formula: Adjective + Subject + Action + Setting." }
          }
        ]
      }
    ]
  };

  const [copiedIndex, setCopiedIndex] = useState(null);

  const copyPrompt = (prompt, index) => {
    navigator.clipboard.writeText(prompt);
    setCopiedIndex(index);
    toast.success("Prompt copied to clipboard!");
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const kidsPrompts = [
    "Cute baby dinosaur hatching from egg in sunny meadow",
    "Friendly dragon blowing bubbles instead of fire",
    "Magical unicorn with rainbow mane jumping over clouds",
    "Happy elephant family playing in water at sunset",
    "Adorable kittens playing with ball of yarn",
    "Brave firefighter puppy on fire truck",
    "Mermaid princess swimming with dolphin friends",
    "Space astronaut cat floating among stars",
    "Fairy princess in enchanted flower garden",
    "Playful monkeys swinging through jungle vines",
    "Cute panda eating bamboo in forest",
    "Baby owl learning to fly from treehouse",
    "Happy robots building sandcastle on beach",
    "Magical wizard casting sparkle spell",
    "Friendly aliens having picnic on moon",
    "Princess and dragon having tea party",
    "Superhero bunny saving the day",
    "Underwater castle with fish and seahorses",
    "Ice cream truck with happy animals in line",
    "Racing cars on rainbow road track"
  ];

  const adultPrompts = [
    "Intricate mandala with lotus flowers and geometric patterns",
    "Zen garden with koi fish pond and cherry blossoms",
    "Detailed peacock feathers in decorative frame",
    "Victorian rose bouquet with ornate borders",
    "Celtic knot pattern with nature elements",
    "Art nouveau inspired butterfly garden",
    "Steampunk mechanical owl with gears and cogs",
    "Japanese wave pattern with detailed fish",
    "Bohemian dreamcatcher with feathers and beads",
    "Detailed hummingbird among tropical flowers",
    "Gothic cathedral window with stained glass pattern",
    "Moroccan tile pattern with geometric shapes",
    "Vintage botanical illustration of succulents",
    "Paisley pattern with hidden animals",
    "Art deco cityscape with geometric buildings"
  ];

  const seasonalPrompts = [
    "Christmas village with snow and decorated trees",
    "Halloween haunted house with friendly ghosts",
    "Easter bunny decorating eggs in garden",
    "Thanksgiving cornucopia with fall harvest",
    "Valentine's Day hearts and love birds",
    "Fourth of July fireworks over city",
    "Back to school supplies with happy face",
    "Spring garden with butterflies and flowers",
    "Summer beach scene with sandcastle",
    "Autumn forest with falling leaves and animals",
    "Winter wonderland with snowman family",
    "New Year celebration with confetti and clock",
    "Mother's Day flower bouquet",
    "Father's Day tools and workshop",
    "Birthday party with cake and balloons"
  ];

  const renderPromptList = (prompts, startNum, color) => {
    const colors = {
      amber: { bg: "bg-amber-100", text: "text-amber-700", hover: "hover:bg-amber-50" },
      violet: { bg: "bg-violet-100", text: "text-violet-700", hover: "hover:bg-violet-50" },
      pink: { bg: "bg-pink-100", text: "text-pink-700", hover: "hover:bg-pink-50" },
      green: { bg: "bg-green-100", text: "text-green-700", hover: "hover:bg-green-50" }
    };
    const colorClasses = colors[color] || colors.amber;

    return (
      <div className="space-y-3">
        {prompts.map((prompt, i) => {
          const globalIndex = startNum + i;
          return (
            <div 
              key={i} 
              className={`flex items-start gap-3 p-4 bg-white rounded-xl border border-slate-100 ${colorClasses.hover} transition-colors group cursor-pointer`}
              onClick={() => copyPrompt(prompt, globalIndex)}
            >
              <span className={`w-8 h-8 rounded-full ${colorClasses.bg} ${colorClasses.text} flex items-center justify-center font-bold text-sm flex-shrink-0`}>
                {globalIndex}
              </span>
              <p className="text-slate-700 flex-1">{prompt}</p>
              <button 
                className={`opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded-lg ${colorClasses.bg}`}
                title="Copy prompt"
              >
                {copiedIndex === globalIndex ? (
                  <Check className={`w-4 h-4 ${colorClasses.text}`} />
                ) : (
                  <Copy className={`w-4 h-4 ${colorClasses.text}`} />
                )}
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-hero-gradient">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                ColorWonder
              </span>
            </Link>

            <div className="flex items-center gap-4">
              <Link to="/blog" className="text-slate-600 hover:text-slate-900 font-medium">
                Blog
              </Link>
              <Button
                onClick={() => window.location.href = "/signup"}
                className="h-10 px-6 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold"
                data-testid="nav-try-free-btn"
              >
                Try Free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Article Content */}
      <article className="pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <Link to="/blog" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 mb-8" data-testid="back-to-blog-link">
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>

          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center gap-3 text-sm text-slate-500 mb-4">
              <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full font-medium">Prompts & Ideas</span>
              <span>•</span>
              <span>Dec 2025</span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                12 min read
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              50 Best Coloring Page Prompts [Free Ideas + Examples]
            </h1>
            
            <p className="text-xl text-slate-600 leading-relaxed">
              Stop searching endlessly for the perfect coloring page. These 50 tested prompts create beautiful, printable coloring pages every time. Click any prompt to copy it, then try it on ColorWonder!
            </p>

            <div className="mt-6 p-4 bg-violet-50 rounded-xl border border-violet-100 flex items-start gap-3">
              <Sparkles className="w-6 h-6 text-violet-600 flex-shrink-0 mt-0.5" />
              <p className="text-violet-800">
                <strong>Pro Tip:</strong> Click any prompt below to copy it instantly. Then paste it into ColorWonder to generate your page in seconds!
              </p>
            </div>
          </header>

          {/* Table of Contents */}
          <div className="bg-slate-50 rounded-2xl p-6 mb-12" data-testid="table-of-contents">
            <h2 className="font-semibold text-slate-800 mb-4">📋 Jump To Section</h2>
            <ul className="space-y-2 text-slate-600">
              <li><a href="#kids" className="hover:text-violet-600">👶 Kids Coloring Page Prompts (1-20)</a></li>
              <li><a href="#adults" className="hover:text-violet-600">🧘 Adult Coloring Page Prompts (21-35)</a></li>
              <li><a href="#seasonal" className="hover:text-violet-600">🎄 Seasonal & Holiday Prompts (36-50)</a></li>
              <li><a href="#tips" className="hover:text-violet-600">💡 Pro Tips for Better Prompts</a></li>
              <li><a href="#colorwonder" className="hover:text-violet-600">✨ Try These Prompts on ColorWonder</a></li>
            </ul>
          </div>

          {/* Introduction */}
          <section className="prose prose-lg max-w-none mb-12">
            <p>
              Finding the perfect coloring page can be frustrating. You search online for hours, only to find the same generic designs. What if you could create <strong>exactly what you're looking for</strong> in seconds?
            </p>
            <p>
              That's where good prompts come in. With AI coloring page generators like ColorWonder, the right prompt creates beautiful, unique coloring pages instantly. We've tested hundreds of prompts to find the <strong>50 that work best</strong>.
            </p>
            <p>
              Below you'll find prompts organized by category: kids, adults, and seasonal themes. Each one has been tested to produce clean, printable results.
            </p>
          </section>

          {/* Section 1: Kids Prompts */}
          <section id="kids" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              👶 Kids Coloring Page Prompts (1-20)
            </h2>
            <p className="text-slate-600 mb-6">
              These prompts create fun, engaging coloring pages perfect for children ages 3-10. Simple designs with bold lines that are easy to color.
            </p>
            
            {renderPromptList(kidsPrompts, 1, "amber")}
          </section>

          {/* Mid-Article CTA */}
          <div className="my-12 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-6 text-white">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5" />
              <span className="font-semibold">Try These Prompts Now</span>
            </div>
            <p className="text-violet-100 mb-4">
              Generate any of these prompts in seconds. Just copy, paste, and watch the magic happen.
            </p>
            <Button
              onClick={() => window.location.href = "/signup"}
              className="h-11 px-6 rounded-full bg-white text-violet-600 font-semibold hover:bg-violet-50"
              data-testid="mid-cta-btn"
            >
              Try ColorWonder Free →
            </Button>
          </div>

          {/* Section 2: Adult Prompts */}
          <section id="adults" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              🧘 Adult Coloring Page Prompts (21-35)
            </h2>
            <p className="text-slate-600 mb-6">
              Intricate designs for stress relief and relaxation. These prompts create detailed patterns perfect for adults who enjoy meditative coloring.
            </p>
            
            {renderPromptList(adultPrompts, 21, "violet")}
          </section>

          {/* Section 3: Seasonal Prompts */}
          <section id="seasonal" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              🎄 Seasonal & Holiday Prompts (36-50)
            </h2>
            <p className="text-slate-600 mb-6">
              Perfect for holidays, seasons, and special occasions. Create themed coloring pages for any time of year.
            </p>
            
            {renderPromptList(seasonalPrompts, 36, "green")}
          </section>

          {/* Pro Tips Section */}
          <section id="tips" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              💡 Pro Tips for Better Prompts
            </h2>
            
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 space-y-4">
              {[
                { tip: "Be Specific", desc: "Instead of 'cat', try 'playful kitten with yarn ball'. More detail = better results." },
                { tip: "Add Adjectives", desc: "Words like 'cute', 'happy', 'friendly', 'magical' create kid-appropriate images." },
                { tip: "Include Actions", desc: "Characters doing something are more engaging: 'dragon flying', 'bunny hopping'." },
                { tip: "Mention Setting", desc: "Add where the scene takes place: 'in garden', 'underwater', 'in space'." },
                { tip: "Specify Style", desc: "Add 'simple for toddlers' or 'detailed for adults' to control complexity." },
                { tip: "Combine Themes", desc: "Mix unexpected ideas: 'astronaut dinosaur', 'mermaid princess tea party'." }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Lightbulb className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-slate-800">{item.tip}:</span>{" "}
                    <span className="text-slate-600">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Prompt Formula */}
          <section className="mb-12">
            <h3 className="text-xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              The Perfect Prompt Formula
            </h3>
            
            <div className="bg-slate-800 rounded-2xl p-6 text-white">
              <code className="text-green-400 text-lg">
                [Adjective] + [Subject] + [Action] + [Setting]
              </code>
              <div className="mt-4 space-y-3">
                <div className="bg-slate-700 rounded-lg p-3">
                  <span className="text-slate-400">Example 1:</span>
                  <p className="text-white">"<span className="text-pink-400">Cute</span> <span className="text-blue-400">baby elephant</span> <span className="text-green-400">splashing</span> <span className="text-amber-400">in waterfall</span>"</p>
                </div>
                <div className="bg-slate-700 rounded-lg p-3">
                  <span className="text-slate-400">Example 2:</span>
                  <p className="text-white">"<span className="text-pink-400">Magical</span> <span className="text-blue-400">unicorn princess</span> <span className="text-green-400">dancing</span> <span className="text-amber-400">in rainbow garden</span>"</p>
                </div>
                <div className="bg-slate-700 rounded-lg p-3">
                  <span className="text-slate-400">Example 3:</span>
                  <p className="text-white">"<span className="text-pink-400">Intricate</span> <span className="text-blue-400">mandala pattern</span> <span className="text-green-400">featuring</span> <span className="text-amber-400">lotus flowers and butterflies</span>"</p>
                </div>
              </div>
            </div>
          </section>

          {/* ColorWonder CTA */}
          <section id="colorwonder" className="mb-12">
            <div className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-8 text-white text-center">
              <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-80" />
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Generate Unlimited Variations with ColorWonder
              </h3>
              <p className="text-violet-100 mb-6 max-w-md mx-auto">
                Turn any of these prompts into beautiful, printable coloring pages in seconds. Just copy, paste, and create!
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-4">
                <Button
                  onClick={() => window.location.href = "/signup"}
                  className="h-14 px-8 rounded-full bg-white text-violet-600 font-semibold text-lg hover:bg-violet-50"
                  data-testid="final-cta-btn"
                >
                  Try ColorWonder Free →
                </Button>
              </div>
              <div className="flex items-center justify-center gap-6 text-violet-200 text-sm">
                <span className="flex items-center gap-2"><Check className="w-4 h-4" /> 10 free pages/month</span>
                <span className="flex items-center gap-2"><Check className="w-4 h-4" /> No credit card</span>
                <span className="flex items-center gap-2"><Check className="w-4 h-4" /> Print instantly</span>
              </div>
            </div>
          </section>

          {/* Bonus: Quick Reference */}
          <section className="mb-12">
            <h3 className="text-xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              📌 Quick Reference: Prompt Categories
            </h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { category: "Animals", examples: "Dinosaurs, unicorns, farm animals, ocean creatures, jungle animals" },
                { category: "Fantasy", examples: "Dragons, fairies, mermaids, wizards, magical castles" },
                { category: "Vehicles", examples: "Fire trucks, spaceships, race cars, trains, airplanes" },
                { category: "Nature", examples: "Flowers, trees, butterflies, gardens, underwater scenes" },
                { category: "Patterns", examples: "Mandalas, geometric shapes, florals, paisley, tribal" },
                { category: "Holidays", examples: "Christmas, Halloween, Easter, Valentine's, Thanksgiving" }
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-4 border border-slate-100">
                  <h5 className="font-semibold text-slate-800 mb-1">{item.category}</h5>
                  <p className="text-sm text-slate-600">{item.examples}</p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {[
                {
                  q: "Can I use these prompts with any AI tool?",
                  a: "These prompts work with most AI image generators, but they're optimized for ColorWonder which is specifically designed to create clean, printable coloring pages."
                },
                {
                  q: "Can I modify these prompts?",
                  a: "Absolutely! These are starting points. Feel free to add your own details, combine prompts, or adjust them to match exactly what you're looking for."
                },
                {
                  q: "What makes a good coloring page prompt?",
                  a: "Good prompts are specific (not generic), include action or setting, and specify the style (simple for kids, detailed for adults). Use the formula: Adjective + Subject + Action + Setting."
                },
                {
                  q: "Are the generated pages free to print?",
                  a: "With ColorWonder's free tier, you get 10 pages per month for personal use. Paid tiers offer more pages and commercial licenses if you want to sell your creations."
                },
                {
                  q: "Can I use these for commercial coloring books?",
                  a: "Yes! With ColorWonder Plus or Pro, you get a commercial license. Generate pages with these prompts and sell them on Amazon, Etsy, or anywhere else."
                }
              ].map((faq, i) => (
                <div key={i} className="bg-white rounded-xl p-5 border border-slate-100">
                  <h4 className="font-semibold text-slate-800 mb-2">{faq.q}</h4>
                  <p className="text-slate-600">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Related Articles */}
          <section className="mb-12">
            <h3 className="font-semibold text-slate-800 mb-4">Related Articles</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/blog/how-to-make-coloring-book" className="p-4 bg-white rounded-xl border border-slate-100 hover:border-violet-200 transition-colors group">
                <h4 className="font-medium text-slate-800 group-hover:text-violet-600">How to Make a Coloring Book →</h4>
                <p className="text-sm text-slate-500">Complete guide to creating your own book</p>
              </Link>
              <Link to="/blog/coloring-book-to-sell" className="p-4 bg-white rounded-xl border border-slate-100 hover:border-violet-200 transition-colors group">
                <h4 className="font-medium text-slate-800 group-hover:text-violet-600">Create Coloring Books to Sell →</h4>
                <p className="text-sm text-slate-500">Turn prompts into profit</p>
              </Link>
            </div>
          </section>

          {/* Author Bio */}
          <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                <Palette className="w-8 h-8 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-800">About ColorWonder</h4>
                <p className="text-slate-600 text-sm mt-1">
                  ColorWonder helps parents, teachers, and creators generate custom coloring pages in seconds. These prompts were tested and curated by our team to ensure the best results.
                </p>
                <Link to="/about" className="text-violet-600 text-sm font-medium mt-2 inline-block hover:underline">
                  Learn more about us →
                </Link>
              </div>
            </div>
          </div>

        </div>
      </article>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">© 2025 ColorWonder. All rights reserved.</p>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <Link to="/privacy" className="hover:text-slate-700">Privacy</Link>
            <Link to="/terms" className="hover:text-slate-700">Terms</Link>
            <Link to="/contact" className="hover:text-slate-700">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Article50BestPrompts2026;
